import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RenovationPage.css';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ServiceFinderModal from '../components/ServiceFinderModal';
import RateLimitMessage from '../components/RateLimitMessage';
import { checkRateLimit, logAICall } from '../lib/rateLimits';

/* ─────────────────────────────────────── constants ── */

const QUALITY_LEVELS = [
  { id: 'budget',  multiplier: 1.0, color: '#10B981' },
  { id: 'mid',     multiplier: 1.6, color: '#B8924A' },
  { id: 'luxury',  multiplier: 2.6, color: '#F59E0B' },
];

// All items used by the report generator + estimates
const RENOVATION_ITEMS = {
  kitchen:  [
    { id: 'cabinets',   label: 'Cabinets & Worktops',  baseCost: 2800, unit: 'set' },
    { id: 'appliances', label: 'Appliances',            baseCost: 1800, unit: 'set' },
    { id: 'tiling',     label: 'Floor & Wall Tiling',   baseCost: 45,   unit: 'm²'  },
    { id: 'plumbing',   label: 'Plumbing',              baseCost: 800,  unit: 'job' },
    { id: 'electrics',  label: 'Electrics',             baseCost: 600,  unit: 'job' },
    { id: 'painting',   label: 'Painting',              baseCost: 12,   unit: 'm²'  },
  ],
  bathroom: [
    { id: 'fixtures',   label: 'Sanitaryware & Taps',   baseCost: 1200, unit: 'set'  },
    { id: 'tiling',     label: 'Floor & Wall Tiling',   baseCost: 55,   unit: 'm²'   },
    { id: 'shower',     label: 'Shower / Bath',         baseCost: 900,  unit: 'unit' },
    { id: 'plumbing',   label: 'Plumbing',              baseCost: 700,  unit: 'job'  },
    { id: 'electrics',  label: 'Electrics & Lighting',  baseCost: 400,  unit: 'job'  },
    { id: 'vanity',     label: 'Vanity & Storage',      baseCost: 600,  unit: 'set'  },
  ],
  bedroom:  [
    { id: 'flooring',   label: 'Flooring',              baseCost: 35,   unit: 'm²'  },
    { id: 'painting',   label: 'Painting',              baseCost: 12,   unit: 'm²'  },
    { id: 'wardrobes',  label: 'Built-in Wardrobes',    baseCost: 1800, unit: 'set' },
    { id: 'electrics',  label: 'Electrics & Lighting',  baseCost: 350,  unit: 'job' },
    { id: 'ac',         label: 'Air Conditioning',      baseCost: 1200, unit: 'unit'},
  ],
  living:   [
    { id: 'flooring',   label: 'Flooring',              baseCost: 38,   unit: 'm²'  },
    { id: 'painting',   label: 'Painting',              baseCost: 12,   unit: 'm²'  },
    { id: 'electrics',  label: 'Electrics & Lighting',  baseCost: 500,  unit: 'job' },
    { id: 'ac',         label: 'Air Conditioning',      baseCost: 1400, unit: 'unit'},
    { id: 'fireplace',  label: 'Fireplace',             baseCost: 2200, unit: 'unit'},
    { id: 'windows',    label: 'Windows & Doors',       baseCost: 650,  unit: 'unit'},
  ],
  exterior: [
    { id: 'landscaping', label: 'Garden & Landscaping', baseCost: 25,    unit: 'm²'  },
    { id: 'pool',        label: 'Swimming Pool',         baseCost: 18000, unit: 'unit'},
    { id: 'paving',      label: 'Paving & Decking',      baseCost: 40,    unit: 'm²'  },
    { id: 'pergola',     label: 'Pergola / Canopy',      baseCost: 3500,  unit: 'unit'},
    { id: 'fencing',     label: 'Fencing & Gates',       baseCost: 180,   unit: 'm'   },
    { id: 'bbq',         label: 'Outdoor Kitchen / BBQ', baseCost: 2800,  unit: 'unit'},
  ],
  whole:    [
    { id: 'structural',  label: 'Structural / Walls',    baseCost: 60,   unit: 'm²'  },
    { id: 'flooring',    label: 'All Flooring',          baseCost: 38,   unit: 'm²'  },
    { id: 'painting',    label: 'Full Paint',            baseCost: 12,   unit: 'm²'  },
    { id: 'kitchen',     label: 'Full Kitchen',          baseCost: 7500, unit: 'set' },
    { id: 'bathrooms',   label: 'Bathrooms (all)',       baseCost: 4500, unit: 'set' },
    { id: 'electrics',   label: 'Full Rewire',           baseCost: 4500, unit: 'job' },
    { id: 'plumbing',    label: 'Full Plumbing',         baseCost: 3500, unit: 'job' },
    { id: 'windows',     label: 'All Windows & Doors',   baseCost: 650,  unit: 'unit'},
    { id: 'ac',          label: 'Full AC System',        baseCost: 5500, unit: 'job' },
  ],
};

// Room areas used for m² calculations (scaled by property size)
const DEFAULT_SIZES = { kitchen: 14, bathroom: 7, bedroom: 18, living: 30, exterior: 60, whole: 120 };

const CITY_MULTIPLIER = { Limassol: 1.15, Nicosia: 1.0, Paphos: 1.05, Larnaca: 0.95, 'Ayia Napa': 1.10, Protaras: 1.05, Paralimni: 0.95 };
const PROPERTY_TYPE_MULT = { apartment: 1.0, house: 1.08, villa: 1.20 };

// ROI data based on Cyprus real estate market research (% return on renovation spend)
const ROOM_ROI = {
  kitchen:  { min: 65, max: 95 },
  bathroom: { min: 60, max: 80 },
  bedroom:  { min: 40, max: 65 },
  living:   { min: 45, max: 70 },
  exterior: { min: 55, max: 120 }, // outdoor/pool high ROI for Cyprus market
  full:     { min: 45, max: 85 },
};
const DISTRICT_ROI_BOOST = { Limassol: 15, Paphos: 10, Nicosia: 0, Larnaca: -5, 'Ayia Napa': 5, Protaras: 5, Paralimni: -5 };
const QUALITY_ROI_BOOST  = { budget: -5, standard: 0, premium: 8 };

const TASK_LABOR_DAYS = {
  cabinets: 3, appliances: 0.5, tiling_k: 0.4, plumbing_k: 2, electrics_k: 1, painting_k: 0.2,
  tiling_b: 0.5, plumbing_bath: 2, electrics_b: 0.5, vanity: 0.5,
  flooring_bed: 0.3, painting_bed: 0.2, wardrobes: 2, electrics_bed: 0.5, ac: 0.5,
  flooring_liv: 0.3, painting_liv: 0.2, electrics_liv: 0.5, ac_liv: 0.5, fireplace: 2, windows: 1,
  landscaping: 0.12, pool: 15, paving: 0.2, pergola: 2.5, fencing: 0.08, bbq: 1.5,
  structural: 0.2, flooring_w: 0.3, painting_w: 0.2, kitchen_w: 6, bath_w: 4,
  electrics_w: 5, plumbing_w: 5, windows_w: 0.5, ac_w: 3,
};

const ITEM_LABOR_KEY = {
  kitchen:  { cabinets:{key:'cabinets',area:false}, appliances:{key:'appliances',area:false}, tiling:{key:'tiling_k',area:true}, plumbing:{key:'plumbing_k',area:false}, electrics:{key:'electrics_k',area:false}, painting:{key:'painting_k',area:true} },
  bathroom: { fixtures:{key:'plumbing_bath',area:false,dedup:true}, tiling:{key:'tiling_b',area:true}, shower:{key:'plumbing_bath',area:false,dedup:true}, plumbing:{key:'plumbing_bath',area:false,dedup:true}, electrics:{key:'electrics_b',area:false}, vanity:{key:'vanity',area:false} },
  bedroom:  { flooring:{key:'flooring_bed',area:true}, painting:{key:'painting_bed',area:true}, wardrobes:{key:'wardrobes',area:false}, electrics:{key:'electrics_bed',area:false}, ac:{key:'ac',area:false} },
  living:   { flooring:{key:'flooring_liv',area:true}, painting:{key:'painting_liv',area:true}, electrics:{key:'electrics_liv',area:false}, ac:{key:'ac_liv',area:false}, fireplace:{key:'fireplace',area:false}, windows:{key:'windows',area:false} },
  exterior: { landscaping:{key:'landscaping',area:true}, pool:{key:'pool',area:false}, paving:{key:'paving',area:true}, pergola:{key:'pergola',area:false}, fencing:{key:'fencing',area:true}, bbq:{key:'bbq',area:false} },
  whole:    { structural:{key:'structural',area:true}, flooring:{key:'flooring_w',area:true}, painting:{key:'painting_w',area:true}, kitchen:{key:'kitchen_w',area:false}, bathrooms:{key:'bath_w',area:false}, electrics:{key:'electrics_w',area:false}, plumbing:{key:'plumbing_w',area:false}, windows:{key:'windows_w',area:false}, ac:{key:'ac_w',area:false} },
};

const ROOM_HINTS = {
  kitchen:  'Avg Cyprus kitchen ~12m²',
  bathroom: 'Avg Cyprus bathroom ~6m²',
  bedroom:  'Avg Cyprus bedroom ~12m²',
  living:   'Avg Cyprus living area ~25m²',
  exterior: 'Typical garden/exterior ~50m²',
};
const MULTI_ROOMS = new Set(['bathroom', 'bedroom']);

const FREE_ROOMS = [
  { id: 'kitchen',  label: 'Kitchen',       icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M2 8h20v13H2zM6 12h.01M6 16h.01M10 12h4M10 16h4"/></svg> },
  { id: 'bathroom', label: 'Bathroom',      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6 Q9 3 12 3 Q15 3 15 6"/><rect x="4" y="6" width="16" height="4" rx="1"/><path d="M4 10v6a4 4 0 0 0 8 0v-6M16 10v3"/><circle cx="17" cy="15" r="2"/></svg> },
  { id: 'bedroom',  label: 'Bedroom',       icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"/><path d="M2 9h20v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/><path d="M6 9V7h4v2M14 9V7h4v2"/></svg> },
  { id: 'living',   label: 'Living Area',   icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="9" rx="2"/><path d="M2 13h20M6 22v-4M18 22v-4M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2"/></svg> },
  { id: 'exterior', label: 'Exterior',      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M2 17c1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0 3 1 4.5 0"/></svg> },
  { id: 'full',     label: 'Full Renovation', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
];

const PROPERTY_TYPES = [
  { id: 'apartment', label: 'Apartment', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M8 21V11h3v10M13 21V11h3v10"/><path d="M2 7h20"/></svg> },
  { id: 'house',     label: 'House',     icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { id: 'villa',     label: 'Villa',     icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M9 21V12h6v9"/><path d="M1 21h22"/></svg> },
];

const QUALITY_OPTIONS = [
  { id: 'budget',   label: 'Budget',   qId: 'budget',  color: '#10B981', desc: 'Functional finishes, basic materials' },
  { id: 'standard', label: 'Standard', qId: 'mid',     color: '#B8924A', desc: 'Mid-range materials, solid quality' },
  { id: 'premium',  label: 'Premium',  qId: 'luxury',  color: '#F59E0B', desc: 'High-end finishes, premium brands' },
];

const SERVICE_CATEGORIES = [
  { id: 'renovator',    label: 'Find Renovator',     icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> },
  { id: 'electrician',  label: 'Find Electrician',   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> },
  { id: 'plumber',      label: 'Find Plumber',       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg> },
  { id: 'tiler',        label: 'Find Tiler',         icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
  { id: 'painter',      label: 'Find Painter',       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 3H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/><path d="M3 9v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9"/><line x1="12" y1="12" x2="12" y2="19"/></svg> },
  { id: 'ac_installer', label: 'Find AC Installer',  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg> },
];

/* ─────────────────────────────────── tips carousel ── */

const ICONS = {
  bulb: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 7 7c0 2.5-1.3 4.7-3.3 6H8.3A7.1 7.1 0 0 1 5 9a7 7 0 0 1 7-7z"/></svg>,
  box: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  wrench: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  trend: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  pool: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20M6 12V8a6 6 0 0 1 12 0v4"/><path d="M2 17c1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0 3 1 4.5 0"/></svg>,
  paint: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 3H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/><path d="M3 9v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9"/><line x1="12" y1="12" x2="12" y2="19"/></svg>,
  window: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>,
  permit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  zap: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  thermo: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>,
  drop: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  home: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  sun: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>,
  wifi: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>,
  tree: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 22V12l-5-9-5 9v10"/><path d="M3 12h18"/></svg>,
  stone: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/></svg>,
  shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  anchor: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/><path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>,
  grid: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
};

// All tips share a subtle neutral palette — no category-based color riot.
const TIP_COLOR  = '#6B7280';
const TIP_BG     = '#FAFAFA';
const TIP_BORDER = '#E5E7EB';

const ALL_TIPS = [
  { idx: 0,  category: 'Budget',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.bulb     },
  { idx: 1,  category: 'Budget',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.box      },
  { idx: 2,  category: 'Budget',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.wrench   },
  { idx: 3,  category: 'Budget',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.calendar },
  { idx: 4,  category: 'Value',     color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.trend    },
  { idx: 5,  category: 'Value',     color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.pool     },
  { idx: 6,  category: 'Value',     color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.paint    },
  { idx: 7,  category: 'Value',     color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.window   },
  { idx: 8,  category: 'Planning',  color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.permit   },
  { idx: 9,  category: 'Planning',  color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.zap      },
  { idx: 10, category: 'Planning',  color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.thermo   },
  { idx: 11, category: 'Planning',  color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.drop     },
  { idx: 12, category: 'Rental',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.home     },
  { idx: 13, category: 'Rental',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.sun      },
  { idx: 14, category: 'Rental',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.wifi     },
  { idx: 15, category: 'Rental',    color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.tree     },
  { idx: 16, category: 'Materials', color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.stone    },
  { idx: 17, category: 'Materials', color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.shield   },
  { idx: 18, category: 'Materials', color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.anchor   },
  { idx: 19, category: 'Materials', color: TIP_COLOR, bg: TIP_BG, border: TIP_BORDER, icon: ICONS.grid     },
];

/* ───────────────────────────────────── calculations ── */

function calcItemCost(item, size, qualityId, city) {
  const qLevel = QUALITY_LEVELS.find(q => q.id === qualityId);
  const cm = CITY_MULTIPLIER[city] || 1.0;
  const base = (item.unit === 'm²' || item.unit === 'm') ? item.baseCost * size : item.baseCost;
  return Math.round(base * (qLevel?.multiplier || 1.0) * cm);
}

function totalForRoom(room, propertyCity) {
  const items = RENOVATION_ITEMS[room.room] || [];
  return room.items.reduce((sum, itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? sum + calcItemCost(item, room.size, room.quality, propertyCity) : sum;
  }, 0);
}

function getEstimate({ propertyType, roomSizes, selectedRooms, roomItems, quality, district }) {
  const qOpt  = QUALITY_OPTIONS.find(q => q.id === quality);
  const qLevel = QUALITY_LEVELS.find(q => q.id === qOpt.qId);
  const cityMult = CITY_MULTIPLIER[district] || 1.0;
  const ptMult   = PROPERTY_TYPE_MULT[propertyType] || 1.0;

  const breakdown = [];
  let total = 0;

  selectedRooms.forEach(roomId => {
    const rm = FREE_ROOMS.find(r => r.id === roomId);
    if (!rm) return;
    const allItems = RENOVATION_ITEMS[roomId === 'full' ? 'whole' : roomId] || [];
    const selectedItemIds = roomItems[roomId] || [];
    const itemsToCalc = allItems.filter(i => selectedItemIds.includes(i.id));
    if (itemsToCalc.length === 0) return;

    const areas = MULTI_ROOMS.has(roomId)
      ? (roomSizes[roomId] || ['6']).map(s => Math.max(1, parseFloat(s) || 1))
      : [Math.max(1, parseFloat(roomSizes[roomId] || (roomId === 'full' ? '0' : '20')) || 1)];

    const cost = areas.reduce((acc, area) =>
      acc + itemsToCalc.reduce((sum, item) => {
        const base = (item.unit === 'm²' || item.unit === 'm') ? item.baseCost * area : item.baseCost;
        return sum + Math.round(base * qLevel.multiplier * cityMult * ptMult);
      }, 0)
    , 0);

    const totalAreaForRoom = areas.reduce((s, a) => s + a, 0);
    total += cost;
    breakdown.push({ id: roomId, label: rm.label, cost, area: Math.round(totalAreaForRoom), itemCount: itemsToCalc.length });
  });

  return { base: total, low: Math.round(total * 0.85), high: Math.round(total * 1.20), breakdown, qualityId: qOpt.qId };
}

function calcRoomLaborDays(roomId, selectedItems, area) {
  const mapping = ITEM_LABOR_KEY[roomId === 'full' ? 'whole' : roomId] || {};
  const seenDedup = new Set();
  let total = 0;
  for (const itemId of selectedItems) {
    const entry = mapping[itemId];
    if (!entry) continue;
    if (entry.dedup) {
      if (seenDedup.has(entry.key)) continue;
      seenDedup.add(entry.key);
    }
    const rate = TASK_LABOR_DAYS[entry.key] || 0;
    total += entry.area ? rate * area : rate;
  }
  return total;
}

function calcDuration(selectedRooms, roomItems, roomSizes) {
  let totalLaborDays = 0;
  for (const roomId of selectedRooms) {
    const items = roomItems[roomId] || [];
    if (MULTI_ROOMS.has(roomId)) {
      const sizes = (roomSizes[roomId] || ['6']).map(s => Math.max(1, parseFloat(s) || 1));
      for (const area of sizes) totalLaborDays += calcRoomLaborDays(roomId, items, area);
    } else {
      const area = Math.max(1, parseFloat(roomSizes[roomId] || (roomId === 'full' ? '120' : '20')) || 1);
      totalLaborDays += calcRoomLaborDays(roomId, items, area);
    }
  }
  const effective = totalLaborDays > 8 ? totalLaborDays / 1.3 : totalLaborDays;
  const low  = Math.floor(effective);
  const high = Math.ceil(effective * 1.4);
  if (high <= 6) return `${low}–${high} days`;
  const lowW  = Math.max(1, Math.floor(effective / 5));
  const highW = Math.ceil(effective * 1.4 / 5);
  return `${lowW}–${highW} weeks`;
}

// Weighted ROI based on which rooms and their relative cost contribution
function getROI(breakdown, quality, district, selectedRooms, roomItems, roomSizes) {
  const totalCost = breakdown.reduce((s, b) => s + b.cost, 0);
  if (totalCost === 0) return null;

  let weightedMin = 0;
  let weightedMax = 0;
  breakdown.forEach(b => {
    const roi = ROOM_ROI[b.id] || ROOM_ROI.full;
    const w = b.cost / totalCost;
    weightedMin += roi.min * w;
    weightedMax += roi.max * w;
  });

  const boost = (DISTRICT_ROI_BOOST[district] || 0) + (QUALITY_ROI_BOOST[quality] || 0);
  const roiMin = Math.round(weightedMin + boost);
  const roiMax = Math.round(weightedMax + boost);
  const addedValueLow  = Math.round(totalCost * (roiMin / 100));
  const addedValueHigh = Math.round(totalCost * (roiMax / 100));
  const duration = calcDuration(selectedRooms, roomItems, roomSizes);

  return { roiMin, roiMax, addedValueLow, addedValueHigh, duration };
}

function buildReportRooms(selectedRooms, roomItems, qualityId, roomSizes) {
  const result = [];
  selectedRooms.forEach(roomId => {
    if (MULTI_ROOMS.has(roomId)) {
      const sizes = (roomSizes[roomId] || ['6']).map(s => Math.max(1, parseFloat(s) || 1));
      sizes.forEach(size => result.push({ room: roomId, size, items: roomItems[roomId] || [], quality: qualityId }));
    } else {
      const s = roomSizes[roomId] || (roomId === 'full' ? '0' : '20');
      result.push({ room: roomId === 'full' ? 'whole' : roomId, size: Math.max(1, parseFloat(s) || 1), items: roomItems[roomId] || [], quality: qualityId });
    }
  });
  return result;
}

/* ─────────────────────────────────── tips carousel ── */

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// eslint-disable-next-line no-unused-vars
function TipsCarousel() {
  const { t } = useLanguage();
  const [tips] = useState(() => shuffleArray(ALL_TIPS).slice(0, 5));
  const [active, setActive] = useState(0);
  const touchStartX = useRef(null);
  const prev = () => setActive(i => (i - 1 + tips.length) % tips.length);
  const next = () => setActive(i => (i + 1) % tips.length);
  const tip = tips[active];
  const tipT = (t('renovation.tips') || [])[tip.idx] || {};
  const categoryT = (t('renovation.tipCategories') || {})[tip.category] || tip.category;
  return (
    <div className="rp-tips">
      <div className="rp-tips__header">
        <div className="rp-tips__title-row">
          <div className="rp-tips__title">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {t('renovation.tipsTitle')}
          </div>
          <div className="rp-tips__counter">{active + 1} / {tips.length}</div>
        </div>
        <div className="rp-tips__dots">
          {tips.map((_, i) => <button key={i} className={`rp-tips__dot ${i === active ? 'active' : ''}`} onClick={() => setActive(i)} />)}
        </div>
      </div>
      <div className="rp-tips__card" style={{ borderColor: tip.border, background: tip.bg }}
        onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (!touchStartX.current) return;
          const d = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(d) > 40) d > 0 ? next() : prev();
          touchStartX.current = null;
        }}>
        <div className="rp-tips__card-top">
          <div className="rp-tips__icon" style={{ color: tip.color, background: tip.color + '18' }}>{tip.icon}</div>
          <span className="rp-tips__category" style={{ color: tip.color, background: tip.color + '18', border: `1px solid ${tip.color}30` }}>{categoryT}</span>
        </div>
        <div className="rp-tips__card-title" style={{ color: tip.color }}>{tipT.title}</div>
        <div className="rp-tips__card-body">{tipT.body}</div>
      </div>
      <div className="rp-tips__nav">
        <button className="rp-tips__nav-btn" onClick={prev}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          {t('renovation.previous')}
        </button>
        <button className="rp-tips__nav-btn rp-tips__nav-btn--next" onClick={next}>
          {t('renovation.next')}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────── page ── */

const IcPin   = ({ size = 11 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IcCheck = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

export default function RenovationPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // form
  const [propertyType, setPropertyType] = useState('apartment');
  const [roomSizes, setRoomSizes] = useState({
    kitchen:  '12',
    bathroom: ['6'],
    bedroom:  ['12'],
    living:   '25',
    exterior: '50',
    full:     '',
  });
  const [sizeErrors, setSizeErrors] = useState({});
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [roomItems, setRoomItems]         = useState({}); // { roomId: [itemId, ...] }
  const [quality, setQuality]             = useState('standard');
  const [district, setDistrict]           = useState('Limassol');

  // results
  const [estimate, setEstimate] = useState(null);

  // service modal (top-of-page service finder)
  const [serviceModal, setServiceModal] = useState(null);

  const [rateLimitResult, setRateLimitResult] = useState(null);

  // Restore draft form state after login redirect
  useEffect(() => {
    const draft = sessionStorage.getItem('rp_draft');
    if (draft) {
      try {
        const s = JSON.parse(draft);
        if (s.propertyType) setPropertyType(s.propertyType);
        if (s.roomSizes)    setRoomSizes(s.roomSizes);
        if (s.selectedRooms) setSelectedRooms(s.selectedRooms);
        if (s.roomItems)    setRoomItems(s.roomItems);
        if (s.quality)      setQuality(s.quality);
        if (s.district)     setDistrict(s.district);
      } catch (_) {}
      sessionStorage.removeItem('rp_draft');
    }
  }, []);

  const toggleRoom = (roomId) => {
    setEstimate(null);
    setSelectedRooms(prev => {
      if (roomId === 'full') {
        if (prev.includes('full')) {
          setRoomItems({});
          return [];
        }
        const allItems = RENOVATION_ITEMS.whole.map(i => i.id);
        setRoomItems({ full: allItems });
        return ['full'];
      }
      const withoutFull = prev.filter(r => r !== 'full');
      if (withoutFull.includes(roomId)) {
        setRoomItems(ri => { const u = { ...ri }; delete u[roomId]; return u; });
        return withoutFull.filter(r => r !== roomId);
      }
      // New room selected — pre-check all its items
      const allItems = (RENOVATION_ITEMS[roomId] || []).map(i => i.id);
      setRoomItems(ri => ({ ...ri, [roomId]: allItems }));
      return [...withoutFull, roomId];
    });
  };

  const toggleRoomItem = (roomId, itemId) => {
    setEstimate(null);
    setRoomItems(prev => {
      const current = prev[roomId] || [];
      const updated = current.includes(itemId)
        ? current.filter(i => i !== itemId)
        : [...current, itemId];
      return { ...prev, [roomId]: updated };
    });
  };

  const anyItemSelected = selectedRooms.some(r => (roomItems[r] || []).length > 0);

  const handleCalculate = async () => {
    if (!anyItemSelected) return;
    const errors = {};
    selectedRooms.forEach(roomId => {
      if (MULTI_ROOMS.has(roomId)) {
        (roomSizes[roomId] || []).forEach((s, i) => {
          if (!s || parseFloat(s) <= 0) errors[`${roomId}_${i}`] = 'Required';
        });
      } else if (!roomSizes[roomId] || parseFloat(roomSizes[roomId]) <= 0) {
        errors[roomId] = 'Size required';
      }
    });
    setSizeErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Rate limit only applies to logged-in users — guests can use the estimator freely.
    if (user?.id) {
      const check = await checkRateLimit('renovation', user.id);
      if (!check.allowed) {
        setRateLimitResult(check);
        await logAICall('renovation', user.id, { blocked: true, blockReason: check.reason });
        return;
      }
    }

    const result = getEstimate({ propertyType, roomSizes, selectedRooms, roomItems, quality, district });
    setEstimate(result);
    if (user?.id) logAICall('renovation', user.id, { model: 'deterministic' });
    setTimeout(() => document.getElementById('rp-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  return (
    <div className="rp-page">

      {/* ── Hero ── */}
      <header className="toolHero">
        <div className="toolHero__topBar">
          <button type="button" className="toolHero__back" onClick={() => navigate(-1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            {t('renovation.back')}
          </button>
        </div>
        <div className="toolHero__inner">
          <span className="toolHero__eyebrow">CYPRUS PROPERTY TOOL</span>
          <h1 className="toolHero__title">Renovation Tool</h1>
          <p className="toolHero__subtitle">Free cost estimator for renovation projects in Cyprus. Select your rooms, choose what needs doing, and get an instant range.</p>
        </div>
      </header>

      <div className="rp-main">
        <div className="rp-layout">

          {/* ── Left ad slot ── */}
          {/* Ad slot hidden for now — restore by removing `style={{ display: 'none' }}`. */}
          <div className="rp-ad-slot" style={{ display: 'none' }}>
            <div className="rp-ad-slot__inner">
              <div className="rp-ad-slot__label">{t('listings.advertisement')}</div>
              <div className="rp-ad-slot__title">{t('listings.placeAd')}</div>
              <div className="rp-ad-slot__sub">{t('listings.adSub')}</div>
              <a href="mailto:cosmoestatescyprus@gmail.com" className="rp-ad-slot__btn">{t('listings.advertise')}</a>
            </div>
          </div>

          {/* ── Main content ── */}
          <div className="rp-builder" style={{ gridColumn: '1 / -1' }}>

            {/* ── Service finder — always visible ── */}
            <div className="rp-services rp-services--top">
              <div className="rp-services__header">
                <div className="rp-services__title">Need a professional?</div>
                <div className="rp-services__sub">Connect with verified Cyprus tradespeople for any renovation task — no estimate needed.</div>
              </div>
              <div className="rp-services__grid">
                {SERVICE_CATEGORIES.map(sc => (
                  <button key={sc.id} className="rp-service-card" onClick={() => setServiceModal({ category: sc.id })}>
                    <div className="rp-service-card__icon">{sc.icon}</div>
                    <div className="rp-service-card__label">{sc.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <p className="rp-disclaimer">
              Indicative estimates only based on typical Cyprus contractor rates (2024–2025). Actual quotes vary by property condition, materials, and contractor. Always get at least 2–3 written quotes before committing. Do not use these figures as price targets in negotiations.
            </p>

            {/* ── Form card ── */}
            <div className="rp-card">
              <div className="rp-form-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                Free Cost Estimator
              </div>

              {/* Property type */}
              <div className="rp-form-section">
                <div className="rp-form-label">Property Type</div>
                <div className="rp-prop-types">
                  {PROPERTY_TYPES.map(pt => (
                    <button key={pt.id} className={`rp-prop-btn${propertyType === pt.id ? ' active' : ''}`} onClick={() => setPropertyType(pt.id)}>
                      <span className="rp-prop-btn__icon">{pt.icon}</span>
                      {pt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room selection */}
              <div className="rp-form-section">
                <div className="rp-form-label">Select Rooms to Renovate <span className="rp-form-label-hint">(pick one or more, then choose what needs work)</span></div>
                <div className="rp-rooms-grid">
                  {FREE_ROOMS.filter(r => !(r.id === 'exterior' && propertyType === 'apartment')).map(r => {
                    const sel = selectedRooms.includes(r.id);
                    const disabled = r.id !== 'full' && selectedRooms.includes('full');
                    return (
                      <button key={r.id}
                        className={`rp-room-chip${sel ? ' active' : ''}${disabled ? ' disabled' : ''}${r.id === 'full' ? ' full' : ''}`}
                        onClick={() => !disabled && toggleRoom(r.id)}>
                        <span className="rp-room-chip__icon">{r.icon}</span>
                        {r.label}
                      </button>
                    );
                  })}
                </div>

                {/* Item selectors — one panel per selected room */}
                {selectedRooms.map(roomId => {
                  const rm = FREE_ROOMS.find(r => r.id === roomId);
                  const itemKey = roomId === 'full' ? 'whole' : roomId;
                  const items = RENOVATION_ITEMS[itemKey] || [];
                  const checkedItems = roomItems[roomId] || [];

                  /* ── size input(s) for this panel ── */
                  const sizeBlock = (() => {
                    if (roomId === 'full') {
                      const err = sizeErrors['full'];
                      return (
                        <div className="rp-size-block">
                          <div className="rp-size-row">
                            <label className="rp-size-label">Total area to renovate</label>
                            <div className="rp-size-input-wrap">
                              <input
                                type="number" className={`rp-size-input${err ? ' rp-size-input--err' : ''}`}
                                placeholder="e.g. 120"
                                value={roomSizes.full}
                                min={1} max={5000}
                                onChange={e => { setRoomSizes(s => ({ ...s, full: e.target.value })); setSizeErrors(se => ({ ...se, full: null })); }}
                              />
                              <span className="rp-size-unit">m²</span>
                            </div>
                            {err && <span className="rp-size-err">{err}</span>}
                          </div>
                        </div>
                      );
                    }
                    if (MULTI_ROOMS.has(roomId)) {
                      const arr = roomSizes[roomId] || ['6'];
                      const hint = ROOM_HINTS[roomId];
                      const label = rm?.label || roomId;
                      return (
                        <div className="rp-size-block">
                          {arr.map((val, idx) => {
                            const errKey = `${roomId}_${idx}`;
                            return (
                              <div key={idx} className="rp-size-row">
                                <label className="rp-size-label">{label}{arr.length > 1 ? ` ${idx + 1}` : ''}</label>
                                <div className="rp-size-input-wrap">
                                  <input
                                    type="number" className={`rp-size-input${sizeErrors[errKey] ? ' rp-size-input--err' : ''}`}
                                    value={val} min={1} max={500}
                                    onChange={e => {
                                      const next = [...arr]; next[idx] = e.target.value;
                                      setRoomSizes(s => ({ ...s, [roomId]: next }));
                                      setSizeErrors(se => { const u = { ...se }; delete u[errKey]; return u; });
                                    }}
                                  />
                                  <span className="rp-size-unit">m²</span>
                                  {arr.length > 1 && (
                                    <button className="rp-size-remove" onClick={() => {
                                      const next = arr.filter((_, i) => i !== idx);
                                      setRoomSizes(s => ({ ...s, [roomId]: next }));
                                    }}>×</button>
                                  )}
                                </div>
                                {sizeErrors[errKey] && <span className="rp-size-err">{sizeErrors[errKey]}</span>}
                              </div>
                            );
                          })}
                          <div className="rp-size-hint">{hint}</div>
                          <button className="rp-size-add" onClick={() => {
                            const def = roomId === 'bathroom' ? '6' : '12';
                            setRoomSizes(s => ({ ...s, [roomId]: [...(s[roomId] || []), def] }));
                          }}>+ Add another {rm?.label?.toLowerCase()}</button>
                        </div>
                      );
                    }
                    // single-instance room
                    const err = sizeErrors[roomId];
                    return (
                      <div className="rp-size-block">
                        <div className="rp-size-row">
                          <label className="rp-size-label">Size</label>
                          <div className="rp-size-input-wrap">
                            <input
                              type="number" className={`rp-size-input${err ? ' rp-size-input--err' : ''}`}
                              value={roomSizes[roomId] ?? ''} min={1} max={5000}
                              onChange={e => { setRoomSizes(s => ({ ...s, [roomId]: e.target.value })); setSizeErrors(se => ({ ...se, [roomId]: null })); }}
                            />
                            <span className="rp-size-unit">m²</span>
                          </div>
                          {err && <span className="rp-size-err">{err}</span>}
                        </div>
                        {ROOM_HINTS[roomId] && <div className="rp-size-hint">{ROOM_HINTS[roomId]}</div>}
                      </div>
                    );
                  })();

                  return (
                    <div key={roomId} className="rp-item-panel">
                      <div className="rp-item-panel__header">
                        <span className="rp-item-panel__icon">{rm?.icon}</span>
                        <span className="rp-item-panel__title">{rm?.label}{roomId !== 'full' ? ' — What needs work?' : ''}</span>
                      </div>
                      {sizeBlock}
                      {roomId !== 'full' && (
                        <div className="rp-item-panel__grid">
                          {items.map(item => {
                            const checked = checkedItems.includes(item.id);
                            return (
                              <button key={item.id}
                                className={`rp-item-toggle${checked ? ' active' : ''}`}
                                onClick={() => toggleRoomItem(roomId, item.id)}>
                                <span className="rp-item-toggle__box">{checked && <IcCheck />}</span>
                                <span className="rp-item-toggle__label">{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Quality */}
              <div className="rp-form-section">
                <div className="rp-form-label">Finish Quality</div>
                <div className="rp-quality-row">
                  {QUALITY_OPTIONS.map(q => (
                    <button key={q.id} className={`rp-qual-btn${quality === q.id ? ' active' : ''}`}
                      onClick={() => setQuality(q.id)}>
                      <span className="rp-qual-btn__label">{q.label}</span>
                      <span className="rp-qual-btn__desc">{q.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* District */}
              <div className="rp-form-section">
                <div className="rp-form-label"><IcPin size={11} /> District</div>
                <div className="rp-city-row">
                  {['Limassol','Nicosia','Paphos','Larnaca','Ayia Napa','Protaras','Paralimni'].map(c => (
                    <button key={c} className={`rp-city-btn${district === c ? ' active' : ''}`} onClick={() => setDistrict(c)}>{c}</button>
                  ))}
                </div>
              </div>

              <button className="rp-calc-btn" disabled={!anyItemSelected} onClick={handleCalculate}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/><line x1="8" y1="6" x2="12" y2="6"/></svg>
                {estimate ? 'Recalculate Estimate' : 'Get My Free Estimate'}
              </button>
              {!anyItemSelected && selectedRooms.length > 0 && (
                <p className="rp-calc-hint">Select at least one item per room to continue</p>
              )}
              {selectedRooms.length === 0 && (
                <p className="rp-calc-hint">Select at least one room above to continue</p>
              )}
              {rateLimitResult && !rateLimitResult.allowed && (
                <RateLimitMessage
                  result={rateLimitResult}
                  feature="renovation"
                  onClose={() => setRateLimitResult(null)}
                />
              )}
            </div>

            {/* ── Results ── */}
            {estimate && (
              <div id="rp-results">

                {/* Cost range — all users */}
                {(() => {
                  const totalRenovArea = Math.round(estimate.breakdown.reduce((s, b) => s + b.area, 0));
                  return (
                    <div className="rp-range-card">
                      <div style={{padding:'12px 16px', background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:10, marginBottom:16, fontSize:13, color:'#78350F', lineHeight:1.5}}>
                        {t('renovation.notAQuoteBanner')}
                      </div>
                      <div className="rp-range-card__header">
                        <div className="rp-range-card__label">
                          <IcPin size={10} /> {district} · {PROPERTY_TYPES.find(p => p.id === propertyType)?.label}
                          {totalRenovArea > 0 && ` · ${totalRenovArea} m²`}
                        </div>
                        <div className="rp-range-card__quality" style={{ color: QUALITY_OPTIONS.find(q => q.id === quality)?.color }}>
                          {QUALITY_OPTIONS.find(q => q.id === quality)?.label} finish
                        </div>
                      </div>
                      <div className="rp-range-card__range">
                        <span className="rp-range-card__low">€{estimate.low.toLocaleString()}</span>
                        <span className="rp-range-card__dash">–</span>
                        <span className="rp-range-card__high">€{estimate.high.toLocaleString()}</span>
                      </div>
                      <div className="rp-range-card__label2">Indicative total renovation cost range</div>

                      <div className="rp-breakdown">
                        <div className="rp-breakdown__title">Breakdown by area</div>
                        {estimate.breakdown.map(b => {
                          const rm = FREE_ROOMS.find(r => r.id === b.id);
                          return (
                            <div key={b.id} className="rp-breakdown__row">
                              <span className="rp-breakdown__label">
                                <span className="rp-breakdown__room-icon">{rm?.icon}</span>
                                {b.label}
                                <span className="rp-breakdown__item-count">({b.itemCount} items · {b.area} m²)</span>
                              </span>
                              <span className="rp-breakdown__val">€{b.cost.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>

                      <div className="rp-range-card__note">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        Budget an additional 10–15% contingency. These figures are for planning only — always get written quotes.
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            {/* TipsCarousel removed — uncomment to re-enable. */}
            {/* <TipsCarousel /> */}
          </div>

          {/* ── Right ad slot ── */}
          {/* Ad slot hidden for now — restore by removing `style={{ display: 'none' }}`. */}
          <div className="rp-ad-slot" style={{ display: 'none' }}>
            <div className="rp-ad-slot__inner">
              <div className="rp-ad-slot__label">{t('listings.advertisement')}</div>
              <div className="rp-ad-slot__title">{t('listings.placeAd')}</div>
              <div className="rp-ad-slot__sub">{t('listings.adSub')}</div>
              <a href="mailto:cosmoestatescyprus@gmail.com" className="rp-ad-slot__btn">{t('listings.advertise')}</a>
            </div>
          </div>

        </div>
      </div>

      {/* Service Finder Modal */}
      {serviceModal && (
        <ServiceFinderModal
          category={serviceModal.category}
          district={district}
          user={user}
          sourceEstimate={estimate ? { propertyType, roomSizes, selectedRooms, quality, district, estimate } : null}
          onClose={() => setServiceModal(null)}
          categoryIcon={SERVICE_CATEGORIES.find(s => s.id === serviceModal.category)?.icon}
        />
      )}

    </div>
  );
}
